import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Sparkles, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";
import { StatusPill, RiskPill } from "./MerchantOverview";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

export default function Transactions() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null); // tx for risk modal
  const [risk, setRisk] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);
  const [cbDialog, setCbDialog] = useState(null);
  const { formatApiError } = useAuth();

  const load = () => api.get("/transactions").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const runRisk = async (tx) => {
    setOpen(tx); setRisk(null); setLoadingAi(true);
    try {
      const { data } = await api.post(`/transactions/${tx.transaction_id}/risk-analysis`);
      setRisk(data);
    } catch (e) {
      toast.error("Risk analysis failed");
    } finally {
      setLoadingAi(false);
    }
  };

  const submitChargeback = async (e, tx) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    try {
      await api.post("/chargebacks", {
        transaction_id: tx.transaction_id,
        reason: fd.get("reason"),
        amount_disputed: parseFloat(fd.get("amount")),
        customer_claim: fd.get("claim"),
      });
      toast.success("Chargeback opened");
      setCbDialog(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid="transactions-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[#00D533]">Ledger</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-2">Transactions</h1>
        </div>
        <Link to="/dashboard/transactions/new">
          <Button className="bg-[#00D533] hover:bg-[#00E636] text-black font-semibold" data-testid="tx-new-btn">
            New Charge
          </Button>
        </Link>
      </div>

      <div className="sk-card overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
          <div className="col-span-3">Transaction</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-1">Risk</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 text-right">Amount</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {rows.length === 0 && (
          <div className="py-16 text-center text-slate-400 text-sm" data-testid="tx-empty">No transactions yet.</div>
        )}
        {rows.map((tx) => (
          <div key={tx.transaction_id} className="grid grid-cols-12 items-center px-4 py-3 text-sm border-b border-slate-100 hover:bg-slate-50">
            <div className="col-span-3 truncate">
              <div className="font-mono text-xs text-slate-400">{tx.transaction_id}</div>
              <div className="text-slate-700">{tx.description || tx.customer_email || "—"}</div>
            </div>
            <div className="col-span-2 capitalize text-slate-600">{tx.type}</div>
            <div className="col-span-1"><RiskPill tier={tx.risk_tier} /></div>
            <div className="col-span-2"><StatusPill status={tx.status} /></div>
            <div className="col-span-2 text-right font-mono">${Number(tx.amount).toFixed(2)}</div>
            <div className="col-span-2 flex justify-end gap-2">
              <Button size="sm" variant="ghost" className="text-xs border border-slate-200 hover:bg-slate-100" onClick={() => runRisk(tx)} data-testid={`tx-ai-${tx.transaction_id}`}>
                <Sparkles className="w-3.5 h-3.5 mr-1" /> AI
              </Button>
              <Button size="sm" variant="ghost" className="text-xs border border-slate-200 hover:bg-slate-100" onClick={() => setCbDialog(tx)} data-testid={`tx-cb-${tx.transaction_id}`}>
                <ArrowLeftRight className="w-3.5 h-3.5 mr-1" /> Dispute
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* AI Risk Modal */}
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="bg-[#FFFFFF] border-slate-200 text-slate-900 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">AI Risk Analysis</DialogTitle>
          </DialogHeader>
          {loadingAi && <div className="py-8 text-center text-slate-500">Running Claude analysis...</div>}
          {risk && (
            <div className="space-y-4" data-testid="risk-result">
              <div className="grid grid-cols-3 gap-4">
                <Stat label="Rule Score" value={`${risk.rule_score}/100`} />
                <Stat label="AI Score" value={`${risk.ai_score}/100`} accent="#00D533" />
                <Stat label="Tier" value={(risk.tier || "").toUpperCase()} accent={risk.tier === "high" ? "#FF4757" : risk.tier === "medium" ? "#F59E0B" : "#10B981"} />
              </div>
              <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                <div className="text-xs uppercase tracking-wider text-slate-400">Recommendation</div>
                <div className="mt-1 text-slate-900 capitalize">{risk.recommended_action?.replace("_", " ")}</div>
                {risk.summary && <p className="mt-3 text-slate-600 text-sm">{risk.summary}</p>}
              </div>
              {risk.reasons?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Rule signals</div>
                  <ul className="space-y-1 text-sm text-slate-600">
                    {risk.reasons.map((r, i) => <li key={i}>• {r}</li>)}
                  </ul>
                </div>
              )}
              {risk.aml_flags?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#FF4757] mb-2">AML Flags</div>
                  <div className="flex flex-wrap gap-2">
                    {risk.aml_flags.map((f, i) => (
                      <span key={i} className="px-2 py-0.5 rounded bg-[#FF4757]/15 text-[#FF4757] text-xs border border-[#FF4757]/30">{f}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Chargeback Dialog */}
      <Dialog open={!!cbDialog} onOpenChange={(v) => !v && setCbDialog(null)}>
        <DialogContent className="bg-[#FFFFFF] border-slate-200 text-slate-900 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Open Chargeback</DialogTitle>
          </DialogHeader>
          {cbDialog && (
            <form onSubmit={(e) => submitChargeback(e, cbDialog)} className="space-y-4" data-testid="cb-form">
              <div className="text-sm text-slate-500">For: <span className="font-mono text-slate-700">{cbDialog.transaction_id}</span> (${Number(cbDialog.amount).toFixed(2)})</div>
              <div>
                <label className="text-sm text-slate-600">Reason</label>
                <input name="reason" required className="mt-1 w-full bg-[#F8FAFC] border border-slate-200 rounded-md px-3 py-2 text-slate-900" placeholder="e.g., Item not received" data-testid="cb-reason" />
              </div>
              <div>
                <label className="text-sm text-slate-600">Amount disputed (USD)</label>
                <input name="amount" required type="number" step="0.01" defaultValue={cbDialog.amount} className="mt-1 w-full bg-[#F8FAFC] border border-slate-200 rounded-md px-3 py-2 text-slate-900" data-testid="cb-amount" />
              </div>
              <div>
                <label className="text-sm text-slate-600">Customer claim</label>
                <textarea name="claim" className="mt-1 w-full bg-[#F8FAFC] border border-slate-200 rounded-md px-3 py-2 text-slate-900 min-h-[80px]" data-testid="cb-claim" />
              </div>
              <Button type="submit" className="bg-[#00D533] hover:bg-[#00E636] text-black font-semibold" data-testid="cb-submit-btn">Open Chargeback</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, accent = "#FFFFFF" }) {
  return (
    <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
      <div className="text-xs uppercase tracking-wider text-slate-400">{label}</div>
      <div className="mt-1 font-heading text-xl" style={{ color: accent }}>{value}</div>
    </div>
  );
}
